import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SuiteRunEditDialog } from '@/features/executions/components/SuiteRunEditDialog';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useSuiteRunEditDialog } from '@/features/executions/components/useSuiteRunEditDialog';
import { useSuiteRunsTableRowActions } from '@/features/executions/components/useSuiteRunsTableRowActions';
import { useTestRunEditDialog } from '@/features/executions/components/useTestRunEditDialog';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { queryTestRuns } from '@/features/executions/data';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  buildSuiteDetailPath,
  buildSuiteRunPath,
  buildTestDetailPath,
  buildTestRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import { TableV2NestedEmptyState } from '@/features/shared/components/tableSystem/TableV2NestedEmptyState';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  buildTableV2NestedBodyRowSx,
  queryAllTableV2NestedRows,
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2NestedCollapse,
  TableV2NameCell,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
  type TableV2Counts,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import {
  TableV2SkeletonRow,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { buildTagColorByName, mergeTagColorByName } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@/ui/primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteRunDto, TestRunDto } from '@flowqa/shared';

type SuiteRunsTableV2Props = {
  active: boolean;
  actionsDisabled?: boolean;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  hideFooter?: boolean;
  initialPageSize?: number;
  linksDisabled?: boolean;
  nameHeaderLabel?: string;
  noRowsText?: string;
  pageSizeOptions?: number[];
  queryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  queryKey: QueryKey;
  requireAuth?: boolean;
  search?: string;
  showActions?: boolean;
  showSuiteName?: boolean;
  tagColorByName?: Partial<Record<string, TagColor>>;
  tagOptionsOverride?: TagOption[];
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  onQueryMeta?: (meta: { total: number; items: SuiteRunDto[] } | null) => void;
};

const suiteRunV2Sorts = [{ field: 'timestamp', direction: 'desc' }] satisfies ApiSort[];
const buildSuiteRunBaseSkeletonColumns = (
  nameColumnKind: TableV2SkeletonColumnKind,
): TableV2SkeletonColumnKind[] => [
  'chevron',
  nameColumnKind,
  'status',
  'passRate',
  'duration',
  'dateTime',
  'tags',
];
const suiteRunSkeletonColumnsWithActions = [
  ...buildSuiteRunBaseSkeletonColumns('name'),
  'actions',
] satisfies TableV2SkeletonColumnKind[];
const testRunSkeletonColumnsWithActions = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];
const testRunSkeletonColumnsWithoutActions = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
] satisfies TableV2SkeletonColumnKind[];

const buildSuiteRunSkeletonColumns = (
  showActions: boolean,
  nameColumnKind: TableV2SkeletonColumnKind,
): TableV2SkeletonColumnKind[] =>
  showActions
    ? nameColumnKind === 'name'
      ? suiteRunSkeletonColumnsWithActions
      : [...buildSuiteRunBaseSkeletonColumns(nameColumnKind), 'actions']
    : buildSuiteRunBaseSkeletonColumns(nameColumnKind);

const buildTestRunSkeletonColumns = (showActions: boolean): TableV2SkeletonColumnKind[] =>
  showActions ? testRunSkeletonColumnsWithActions : testRunSkeletonColumnsWithoutActions;

const countSuiteRunTests = (row: SuiteRunDto): TableV2Counts => {
  const passed = Math.max(
    0,
    (row.tests ?? 0) - (row.failures ?? 0) - (row.errors ?? 0) - (row.skipped ?? 0),
  );
  return {
    total: row.tests ?? 0,
    passed,
    failed: row.failures ?? 0,
    errored: row.errors ?? 0,
    skipped: row.skipped ?? 0,
    untested: 0,
  };
};

const buildColgroup = (showActions: boolean): ReactElement => (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width={showActions ? '20%' : '22%'} />
    <col width={showActions ? '9%' : '10%'} />
    <col width={showActions ? '16%' : '17%'} />
    <col width="11%" />
    <col width={showActions ? '20%' : '21%'} />
    <col width={showActions ? '20%' : '22%'} />
    {showActions ? <col width={tableV2ActionsColumnWidth} /> : null}
  </colgroup>
);

const buildTestRunColgroup = (showActions: boolean): ReactElement => (
  <colgroup>
    <col width={showActions ? '24%' : '26%'} />
    <col width="11%" />
    <col width="13%" />
    <col width={showActions ? '24%' : '25%'} />
    <col width={showActions ? '22%' : '25%'} />
    {showActions ? <col width={tableV2ActionsColumnWidth} /> : null}
  </colgroup>
);

const buildHeader = (nameHeaderLabel: string, showActions: boolean): ReactElement => (
  <TableRow>
    <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
    <TableCell>{nameHeaderLabel}</TableCell>
    <TableCell>Status</TableCell>
    <TableCell>Pass Rate</TableCell>
    <TableCell>Duration</TableCell>
    <TableCell>Ran At</TableCell>
    <TableCell>Tags</TableCell>
    {showActions ? (
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    ) : null}
  </TableRow>
);

const buildTestRunHeader = (showActions: boolean): ReactElement => (
  <TableRow>
    <TableCell>Test Run</TableCell>
    <TableCell>Status</TableCell>
    <TableCell>Duration</TableCell>
    <TableCell>Ran At</TableCell>
    <TableCell>Tags</TableCell>
    {showActions ? (
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    ) : null}
  </TableRow>
);

const TestRunNestedRow = ({
  actionsDisabled,
  deleteRowPendingId,
  linksDisabled,
  row,
  showActions,
  tagColorByName,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  linksDisabled: boolean;
  row: TestRunDto;
  showActions: boolean;
  tagColorByName?: Partial<Record<string, TagColor>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestRunDto) => void;
  onEditRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => (
  <TableRow sx={buildTableV2NestedBodyRowSx({ depth: 1, hasRenderedDivider: true })}>
    <TableCell>
      <TableV2NameCell
        href={linksDisabled ? null : buildTestDetailPath(row.testSlug ?? row.testId)}
        primary={row.displayName ?? row.testName ?? row.name ?? row.id}
        secondary={formatTableV2RunNumber(row.runNumber)}
        secondaryHref={
          linksDisabled ? null : buildTestRunPath(row.testSlug ?? row.testId, row.runNumber)
        }
      />
    </TableCell>
    <TableCell>
      <TableV2StatusChip status={row.status} />
    </TableCell>
    <TableCell>
      <TableV2DurationCell value={row.durationMs} />
    </TableCell>
    <TableCell>
      <TableV2DateTimeCell value={row.timestamp} />
    </TableCell>
    <TableCell>
      <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
    </TableCell>
    {showActions ? (
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        {actionsDisabled ? null : (
          <TableActionsCell
            rowName={row.displayName ?? row.testName ?? row.name ?? row.id}
            verticalAlign="center"
            deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
            editActionLabel={getEntityActionLabel('edit', 'Test Run')}
            editTagsEntityNoun="test run"
            rowTags={row.tags ?? []}
            onEdit={() => {
              onEditRow(row);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
            onDelete={() => {
              onDeleteRow(row);
            }}
            disableDelete={deleteRowPendingId === row.id}
          />
        )}
      </TableCell>
    ) : null}
  </TableRow>
);

const SuiteRunExpandedTests = ({
  actionsDisabled,
  childCount,
  deleteRowPendingId,
  expanded,
  linksDisabled,
  organisationId,
  projectId,
  showActions,
  suiteRunId,
  tagColorByName,
  testRunsQueryFn,
  updateRowTagsColorPendingId,
  onDeleteTestRunRow,
  onEditTestRunRow,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  childCount: number;
  deleteRowPendingId: string | null;
  expanded: boolean;
  linksDisabled: boolean;
  organisationId: string | null;
  projectId: string;
  showActions: boolean;
  suiteRunId: string;
  tagColorByName?: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onEditTestRunRow: (row: TestRunDto) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.testRuns,
      'v2-nested',
      organisationId,
      projectId,
      suiteRunId,
    ),
    enabled: expanded,
    queryFn: async () => {
      const input = {
        page: 1,
        pageSize: 1,
        sorts: [{ field: 'timestamp', direction: 'desc' as const }],
        filters: buildEqFilter('suiteRunId', suiteRunId),
      };
      return await queryAllTableV2NestedRows(
        input,
        testRunsQueryFn ??
          (async (nextInput) => await queryTestRuns(projectId, organisationId, nextInput)),
      );
    },
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Test runs loading" sx={tableV2NestedDepthSx[1].table}>
              {buildTestRunColgroup(showActions)}
              <TableHead>{buildTestRunHeader(showActions)}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`test-runs-nested-skeleton-${String(index)}`}
                    columns={buildTestRunSkeletonColumns(showActions)}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : query.data && query.data.items.length > 0 ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Test runs" sx={tableV2NestedDepthSx[1].table}>
              {buildTestRunColgroup(showActions)}
              <TableHead>{buildTestRunHeader(showActions)}</TableHead>
              <TableBody>
                {query.data.items.map((testRun) => (
                  <TestRunNestedRow
                    key={testRun.id}
                    actionsDisabled={actionsDisabled}
                    deleteRowPendingId={deleteRowPendingId}
                    linksDisabled={linksDisabled}
                    row={testRun}
                    showActions={showActions}
                    tagColorByName={tagColorByName}
                    updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                    onDeleteRow={onDeleteTestRunRow}
                    onEditRow={onEditTestRunRow}
                    onUpdateRowTagsColor={onUpdateTestRunRowTagsColor}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel="Test runs"
            colgroup={buildTestRunColgroup(showActions)}
            colSpan={showActions ? 6 : 5}
            depth={1}
            header={buildTestRunHeader(showActions)}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};

const SuiteRunRow = ({
  actionsDisabled,
  deleteRowPendingId,
  isLastVisibleRow,
  linksDisabled,
  row,
  showActions,
  showSuiteName,
  tagColorByName,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  testRunsQueryFn,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteTestRunRow,
  onEditRow,
  onEditTestRunRow,
  onUpdateRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  linksDisabled: boolean;
  row: SuiteRunDto;
  showActions: boolean;
  showSuiteName: boolean;
  tagColorByName?: Partial<Record<string, TagColor>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onEditRow: (row: SuiteRunDto) => void;
  onEditTestRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: SuiteRunDto; tags: string[]; colors: TagColor[] }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const name = row.displayName ?? row.suiteName ?? row.name ?? row.id;
  const detailPath = buildSuiteDetailPath(row.suiteSlug ?? row.suiteId);
  const runPath = buildSuiteRunPath(row.suiteSlug ?? row.suiteId, row.runNumber);
  const runNumberLabel = formatTableV2RunNumber(row.runNumber);
  const primaryLabel = showSuiteName ? name : (runNumberLabel ?? name);
  const primaryHref = showSuiteName ? detailPath : runPath;
  const secondaryLabel = showSuiteName ? runNumberLabel : null;
  const secondaryHref = showSuiteName ? runPath : null;
  const testCount = countSuiteRunTests(row).total;

  return (
    <>
      <TableRow
        sx={buildTableV2BodyRowSurfaceSx({
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse test runs' : 'Expand test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={linksDisabled ? null : primaryHref}
            primary={primaryLabel}
            secondary={secondaryLabel}
            secondaryHref={linksDisabled ? null : secondaryHref}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell counts={countSuiteRunTests(row)} />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={row.durationMs} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.timestamp} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showActions ? (
          <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
            {actionsDisabled ? null : (
              <TableActionsCell
                rowName={name}
                verticalAlign="center"
                deleteActionLabel={getEntityActionLabel('delete', 'Suite Run')}
                editActionLabel={getEntityActionLabel('edit', 'Suite Run')}
                editTagsEntityNoun="suite run"
                rowTags={row.tags ?? []}
                onEdit={() => {
                  onEditRow(row);
                }}
                onUpdateTagsColor={(input) => {
                  onUpdateRowTagsColor({ row, ...input });
                }}
                updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
                onDelete={() => {
                  onDeleteRow(row);
                }}
                disableDelete={deleteRowPendingId === row.id}
              />
            )}
          </TableCell>
        ) : null}
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={showActions ? 8 : 7}>
          <SuiteRunExpandedTests
            actionsDisabled={actionsDisabled}
            childCount={testCount}
            deleteRowPendingId={testRunDeleteRowPendingId}
            expanded={expanded}
            linksDisabled={linksDisabled}
            organisationId={row.organisationId}
            projectId={row.projectId}
            showActions={showActions}
            suiteRunId={row.id}
            tagColorByName={tagColorByName}
            testRunsQueryFn={testRunsQueryFn}
            updateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onEditTestRunRow={onEditTestRunRow}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const SuiteRunsTableV2 = ({
  actionsDisabled = false,
  active,
  fixedFilters,
  forceLoading = false,
  hideFooter = false,
  initialPageSize,
  linksDisabled = false,
  nameHeaderLabel = 'Suite Run',
  noRowsText = 'No data',
  onQueryMeta,
  pageSizeOptions,
  queryFn,
  queryKey,
  requireAuth = true,
  search,
  showActions = true,
  showSuiteName = true,
  tagColorByName,
  tagOptionsOverride = [],
  testRunsQueryFn,
}: SuiteRunsTableV2Props): ReactElement => {
  const { selectedProjectId } = useProjectFilter();
  const resolvedTagColorByName = useMemo(
    () => mergeTagColorByName(buildTagColorByName(tagOptionsOverride), tagColorByName),
    [tagColorByName, tagOptionsOverride],
  );
  const { editLoading, editTarget, onEditClose, onEditRow, onEditSave } =
    useSuiteRunEditDialog(selectedProjectId);
  const {
    editLoading: testRunEditLoading,
    editTarget: testRunEditTarget,
    onEditClose: onTestRunEditClose,
    onEditRow: onEditTestRunRow,
    onEditSave: onTestRunEditSave,
  } = useTestRunEditDialog(selectedProjectId);
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useSuiteRunsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: testRunDeleteLoading,
    deleteRowPendingId: testRunDeleteRowPendingId,
    deleteTarget: testRunDeleteTarget,
    onDeleteClose: onTestRunDeleteClose,
    onDeleteConfirm: onTestRunDeleteConfirm,
    onDeleteRow: onDeleteTestRunRow,
    onUpdateRowTagsColor: onUpdateTestRunRowTagsColor,
    updateRowTagsColorPendingId: testRunUpdateRowTagsColorPendingId,
  } = useTestRunsTableRowActions({ selectedProjectId });
  const suiteRunNameSkeletonKind: TableV2SkeletonColumnKind =
    nameHeaderLabel === 'Suite' ? 'nameSingle' : 'name';

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Suite runs"
        colgroup={buildColgroup(showActions)}
        emptyText={noRowsText}
        entityLabel="suite runs"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={buildHeader(nameHeaderLabel, showActions)}
        hideFooter={hideFooter}
        initialPageSize={initialPageSize}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        requireAuth={requireAuth}
        search={search}
        skeletonColumns={buildSuiteRunSkeletonColumns(showActions, suiteRunNameSkeletonKind)}
        sorts={suiteRunV2Sorts}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <SuiteRunRow
            key={row.id}
            actionsDisabled={actionsDisabled}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            linksDisabled={linksDisabled}
            row={row}
            showActions={showActions}
            showSuiteName={showSuiteName}
            tagColorByName={resolvedTagColorByName}
            testRunDeleteRowPendingId={testRunDeleteRowPendingId}
            testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            testRunsQueryFn={testRunsQueryFn}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onEditRow={onEditRow}
            onEditTestRunRow={onEditTestRunRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        )}
      />
      <SuiteRunEditDialog
        open={editTarget !== null}
        execution={editTarget}
        loading={editLoading}
        onClose={onEditClose}
        onSave={onEditSave}
      />
      <TestRunEditDialog
        open={testRunEditTarget !== null}
        execution={testRunEditTarget}
        loading={testRunEditLoading}
        onClose={onTestRunEditClose}
        onSave={onTestRunEditSave}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'suiteRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'suiteRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'suiteRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={testRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={testRunDeleteLoading}
        onClose={onTestRunDeleteClose}
        onConfirm={onTestRunDeleteConfirm}
      />
    </>
  );
};
